/** @jsxImportSource @emotion/react */
import React, { useEffect, useState } from 'react';
import { theme } from 'styles/theme/theme';
import type { DivProps } from 'typings';
import Text from 'ud-ui/components/text';
import * as S from './styles';

export interface RankedBarItem {
  id: number | string;
  image: string;
  label: string;
  value: number;
  valueText: React.ReactNode;
}

interface IProps extends DivProps {
  items: RankedBarItem[];
  /** Цвет заливки бара — акцент проекта по умолчанию. */
  color?: string;
  /**
   * Строка становится кнопкой: подсветка при наведении, фокус с клавиатуры, Enter/Space. Без него
   * строки остаются просто подписью.
   */
  onItemClick?: (item: RankedBarItem) => void;
}

/**
 * Мини-диаграмма «команда/игрок → бар»: ширина бара — доля значения от максимума в списке,
 * а не от абсолютной шкалы — так лидер списка всегда даёт наглядные 100%.
 *
 * Заполнение анимируется от 0 при монтировании (совпадает с моментом, когда скелетон карточки
 * сменяется данными), построчно, с нарастающей задержкой — вместо отрисовки сразу готовых баров.
 */
function RankedBarList(props: IProps) {
  const { items, color = theme.colors.DarkYellow, onItemClick, ...rest } = props;
  const [filled, setFilled] = useState(false);

  useEffect(() => {
    // Двойной rAF: первый кадр коммитит ширину 0%, второй — переключает класс, чтобы
    // браузер успел применить исходное состояние до начала CSS-перехода.
    const raf1 = requestAnimationFrame(() => {
      const raf2 = requestAnimationFrame(() => setFilled(true));
      return () => cancelAnimationFrame(raf2);
    });
    return () => cancelAnimationFrame(raf1);
  }, []);

  const maxValue = Math.max(...items.map((item) => item.value), 1);

  return (
    <S.MainBlock {...rest}>
      {items.map((item, index) => {
        // Нижняя граница в 6% — чтобы нулевое или совсем маленькое значение оставалось
        // видимой полоской, а не пропадало на фоне трека.
        const widthPercent = Math.max(6, Math.round((item.value / maxValue) * 100));

        const rowProps = onItemClick
          ? {
              className: 'clickable',
              role: 'button',
              tabIndex: 0,
              // Карточка секции сама ведёт на список по клику — клик по строке не должен ещё и туда.
              onClick: (event: React.MouseEvent) => {
                event.stopPropagation();
                onItemClick(item);
              },
              onKeyDown: (event: React.KeyboardEvent) => {
                if (event.key !== 'Enter' && event.key !== ' ') return;
                event.preventDefault();
                event.stopPropagation();
                onItemClick(item);
              },
            }
          : undefined;

        return (
          <S.Row key={item.id} {...rowProps}>
            <S.Avatar>
              <img src={item.image} alt="" />
            </S.Avatar>
            <S.Bar>
              <Text variant="subtitle-1" className="truncate" color={theme.colors.Gray}>
                {item.label}
              </Text>
              <S.Track>
                <S.Fill
                  className={filled ? 'filled' : undefined}
                  $color={color}
                  $delayMs={index * 90}
                  style={{ '--target-width': `${widthPercent}%` } as React.CSSProperties}
                />
              </S.Track>
            </S.Bar>
            <Text variant="subtitle-2-bold" color={color} style={{ whiteSpace: 'nowrap' }}>
              {item.valueText}
            </Text>
          </S.Row>
        );
      })}
    </S.MainBlock>
  );
}

export default RankedBarList;
